import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Role, SessionUser } from '@aviaconsole/shared';
import { api, setCompanyId } from '../api/client';

export interface CompanyOption { id: string; name: string; shortName: string; slug: string; role: Role; customerId: string | null; crmEnabled: boolean }
interface Me { user: SessionUser | null; companies: CompanyOption[] }

interface AuthCtx {
  loading: boolean; user: SessionUser | null; companies: CompanyOption[];
  company: CompanyOption | null; setCompany: (id: string) => void; role: Role | null;
  login: (returnTo?: string) => void; logout: () => Promise<void>; refresh: () => Promise<void>;
}

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const me = useQuery({ queryKey: ['me'], queryFn: () => api<Me>('/api/auth/me'), staleTime: 60_000 });
  const [companyId, setCompanyState] = useState<string | null>(() => { try { return localStorage.getItem('ld.company'); } catch { return null; } });

  const companies = me.data?.companies ?? [];
  const company = useMemo(() => {
    const c = companies.find((x) => x.id === companyId) ?? companies[0] ?? null;
    // Synchronously, so queries rendered in the same pass already carry the company.
    setCompanyId(c?.id ?? null);
    return c;
  }, [companies, companyId]);

  const setCompany = useCallback((id: string) => {
    setCompanyState(id);
    try { localStorage.setItem('ld.company', id); } catch { /* ignore */ }
    setCompanyId(id);
    qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'me' });
  }, [qc]);

  const value: AuthCtx = {
    loading: me.isLoading, user: me.data?.user ?? null, companies, company, setCompany, role: company?.role ?? null,
    login: (returnTo = '/') => { window.location.href = `/api/auth/login?returnTo=${encodeURIComponent(returnTo)}`; },
    logout: async () => {
      const r = await api<{ ok: boolean; endSessionUrl: string | null }>('/api/auth/logout', { method: 'POST' });
      qc.clear();
      window.location.href = r.endSessionUrl ?? '/';
    },
    refresh: async () => { await qc.invalidateQueries({ queryKey: ['me'] }); },
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth outside AuthProvider');
  return v;
}
